// src/components/notify/NotifyProvider.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense Notify — mount ONCE near the app root (inside ChakraProvider):
//
//   const navigate = useNavigate();               // react-router (optional)
//   <NotifyProvider navigate={navigate} enabled={isLoggedIn}>
//     <App />                                     // put <NotificationBell /> in the navbar
//   </NotifyProvider>
//
// What it does:
//   • opens the shared live stream (services/notifyStream) and keeps the unread summary current
//   • toasts new notifications (critical: stays until closed + Acknowledge button); bursts of
//     more than 3 in 4 s collapse into one "N new notifications" toast
//   • plays a short tone for high / critical (WebAudio — no asset), desktop pop-up when the tab
//     is hidden and browser push is not enabled on this device (avoids duplicates)
//   • "(3) FlowSense" unread count in the tab title
//   • opens the notification centre drawer; handles clicks from the service worker
//   • bridges to existing FlowSense widgets: window 'flowsense:notify' (every notification) and
//     'flowsense:sla-ai-inbox' (SLA AI notifications → the SLA AI inbox refreshes)
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CloseButton, Flex, HStack, Icon, Text, useColorModeValue, useDisclosure, useToast } from '@chakra-ui/react';
import { MdCheckCircle, MdOpenInNew } from 'react-icons/md';
import notifyStream from '../../services/notifyStream';
import * as api from '../../services/notifyApi';
import * as webPush from '../../utils/notifyWebPush';
import NotificationCenter from './NotificationCenter';
import { categoryMeta, priorityMeta } from './notifyFormat';

const NotifyContext = createContext(null);

// --- UPDATE (toast once) ------------------------------------------------------------
// Every notification pops up ONCE — not again after a reload, a reconnect, in a second tab,
// or when an automated job sends the identical message again a few minutes later.
//   • id ledger (24 h) in localStorage, shared by every tab of this browser
//   • content ledger (title + body, 30 min): an identical message with a new id updates the
//     badge / inbox silently instead of popping up again
//   • only a VISIBLE tab shows the toast; when no tab is visible the browser push (service
//     worker) shows the OS notification instead — never both
//   • a toast closes itself when the notification is read / acknowledged / archived anywhere
const LEDGER_KEY = 'fsn:toasted:v1';
const ID_TTL_MS = 24 * 3600 * 1000;
const REPEAT_TTL_MS = 30 * 60 * 1000;
let memLedger = {};
const ledger = {
    read() {
        try { const raw = window.localStorage.getItem(LEDGER_KEY); return raw ? JSON.parse(raw) : memLedger; } catch (_) { return memLedger; }
    },
    write(map) {
        memLedger = map;
        try { window.localStorage.setItem(LEDGER_KEY, JSON.stringify(map)); } catch (_) { /* private mode: memory only */ }
    },
    /** true the first time `key` is claimed inside its TTL (in any tab of this browser) */
    claim(key, ttlMs, now = Date.now()) {
        const map = ledger.read();
        if (map[key] && map[key] > now) return false;
        const next = {};
        Object.entries(map).forEach(([k, exp]) => { if (exp > now) next[k] = exp; });
        const keys = Object.keys(next);
        if (keys.length > 400) keys.sort((a, b) => next[a] - next[b]).slice(0, keys.length - 400).forEach(k => delete next[k]);
        next[key] = now + ttlMs;
        ledger.write(next);
        return true;
    },
    has(key, now = Date.now()) { const exp = ledger.read()[key]; return Boolean(exp && exp > now); },
};
const fingerprint = (n) => {
    const str = `${n.title || ''}\u0001${n.body || n.message || ''}`;
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0; // djb2
    return `fp:${(h >>> 0).toString(36)}`;
};
const tabVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
export const __toastLedger = ledger; // tests
// --- END UPDATE ------------------------------------------------------------------------

const FALLBACK = Object.freeze({
    summary: null, status: 'idle', isCenterOpen: false,
    openCenter: () => {}, closeCenter: () => {}, refreshSummary: () => Promise.resolve(null), navigateTo: () => {},
});

export const useNotify = () => useContext(NotifyContext) || FALLBACK;

let audioCtx = null;
function playTone(critical) {
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtx = audioCtx || new AC();
        const now = audioCtx.currentTime;
        (critical ? [880, 660, 880] : [740]).forEach((freq, i) => {
            const o = audioCtx.createOscillator();
            const g = audioCtx.createGain();
            o.type = 'sine';
            o.frequency.value = freq;
            g.gain.setValueAtTime(0.0001, now + i * 0.18);
            g.gain.exponentialRampToValueAtTime(0.18, now + i * 0.18 + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 0.16);
            o.connect(g).connect(audioCtx.destination);
            o.start(now + i * 0.18);
            o.stop(now + i * 0.18 + 0.17);
        });
    } catch (_) { /* autoplay blocked until the user interacts — fine */ }
}

function NotifyToast({ n, count, onClose, onOpen, onAck }) {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.600');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const cat = categoryMeta(n?.category);
    const pr = priorityMeta(n?.priority);
    const accent = n?.priority === 'critical' ? 'red.500' : n?.priority === 'high' ? 'orange.400' : `${cat.color}.400`;
    const [acking, setAcking] = useState(false);
    if (count) {
        return (
            <Box role="status" bg={bg} borderWidth="1px" borderColor={border} borderLeftWidth="4px" borderLeftColor="purple.500" borderRadius="lg" boxShadow="lg" p={3} w="sm" maxW="100%">
                <Flex justify="space-between" align="center" gap={2}>
                    <Text fontWeight="700" fontSize="sm">{count} new notifications</Text>
                    <HStack><Button size="xs" colorScheme="purple" onClick={() => { onOpen(); onClose(); }}>View</Button><CloseButton size="sm" onClick={onClose} /></HStack>
                </Flex>
            </Box>
        );
    }
    return (
        <Box role="alert" bg={bg} borderWidth="1px" borderColor={border} borderLeftWidth="4px" borderLeftColor={accent} borderRadius="lg" boxShadow="lg" p={3} w="sm" maxW="100%">
            <Flex gap={2} align="flex-start">
                <Icon as={cat.icon} color={`${cat.color}.500`} mt={0.5} />
                <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="800" noOfLines={2}>{n.title}</Text>
                    {n.body && n.body !== n.title && <Text fontSize="xs" color={muted} noOfLines={2} mt={0.5}>{n.body}</Text>}
                    <HStack mt={2} spacing={2}>
                        {n.requires_ack && !n.acknowledged_at && (
                            <Button size="xs" colorScheme={n.priority === 'critical' ? 'red' : 'purple'} leftIcon={<MdCheckCircle />} isLoading={acking}
                                onClick={async () => { setAcking(true); try { await onAck(n); onClose(); } finally { setAcking(false); } }}>
                                Acknowledge
                            </Button>
                        )}
                        <Button size="xs" variant="ghost" rightIcon={<MdOpenInNew />} onClick={() => { onOpen(n); onClose(); }}>Open</Button>
                        {n.priority !== 'normal' && <Text fontSize="2xs" color={`${pr.color}.500`} fontWeight="700" textTransform="uppercase">{pr.label}</Text>}
                    </HStack>
                </Box>
                <CloseButton size="sm" onClick={onClose} aria-label="Dismiss notification" />
            </Flex>
        </Box>
    );
}

export function NotifyProvider({ children, navigate, enabled = true }) {
    const toast = useToast();
    const { isOpen: centreOpen, onOpen: centreOnOpen, onClose: centreOnClose } = useDisclosure();
    const [centreMounted, setCentreMounted] = useState(false);
    const [summary, setSummary] = useState(null);
    const [status, setStatus] = useState(notifyStream.getStatus());
    const isMounted = useRef(true);
    const seen = useRef(new Set());
    const burst = useRef([]);
    const openToasts = useRef(new Set()); // --- UPDATE (toast once)
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const baseTitle = useRef(typeof document !== 'undefined' ? document.title.replace(/^\(\d+\+?\)\s*/, '') : '');
    useEffect(() => () => { isMounted.current = false; }, []);

    // react-router's navigate can change identity on every route change — read it through a ref so
    // the live stream is never torn down by navigation
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const navigateTo = useCallback((url) => {
        if (!url) return;
        if (/^https?:\/\//i.test(url)) { window.open(url, '_blank', 'noopener'); return; }
        if (typeof navigateRef.current === 'function') navigateRef.current(url);
        else window.location.assign(url);
    }, []);

    const refreshSummary = useCallback(async () => {
        try {
            const s = await api.getSummary();
            if (isMounted.current) setSummary(s);
            return s;
        } catch (_) { return null; }
    }, []);

    const openCenter = useCallback(() => { setCentreMounted(true); centreOnOpen(); }, [centreOnOpen]);

    const openNotification = useCallback((n) => {
        if (!n) { openCenter(); return; }
        if (!n.is_read) api.markRead([n.id]).catch(() => {});
        const url = n.action_url || (n.entity?.type === 'work_order' ? `/admin/work-orders?workOrderId=${encodeURIComponent(n.entity.id)}` : null);
        if (url) navigateTo(url); else openCenter();
    }, [navigateTo, openCenter]);

    const ack = useCallback(async (n) => {
        try {
            await api.acknowledge(n.id);
            toast({ status: 'success', title: 'Acknowledged', duration: 2000 });
        } catch (e) {
            toast({ status: 'error', title: 'Could not acknowledge', description: e.message });
            throw e;
        }
    }, [toast]);

    const showToast = useCallback((n) => {
        const now = Date.now();
        burst.current = burst.current.filter(t => now - t < 4000);
        burst.current.push(now);
        if (burst.current.length > 3) {
            const count = burst.current.length;
            const id = 'notify-burst';
            const render = ({ onClose }) => <NotifyToast count={count} onClose={onClose} onOpen={openCenter} />;
            if (toast.isActive(id)) toast.update(id, { render, duration: 6000 });
            else toast({ id, position: 'top-right', duration: 6000, render });
            return;
        }
        const toastId = `notify-${n.id}`;
        if (toast.isActive(toastId)) return; // --- UPDATE (toast once)
        openToasts.current.add(n.id);
        toast({
            id: toastId,
            position: 'top-right',
            duration: n.priority === 'critical' || (n.requires_ack && !n.acknowledged_at) ? null : n.priority === 'high' ? 9000 : 6000,
            onCloseComplete: () => { openToasts.current.delete(n.id); },
            render: ({ onClose }) => <NotifyToast n={n} onClose={onClose} onOpen={openNotification} onAck={ack} />,
        });
    }, [toast, openCenter, openNotification, ack]);

    const handleNew = useCallback((payload) => {
        const n = payload?.notification;
        if (!n || seen.current.has(n.id)) return;
        seen.current.add(n.id);
        if (seen.current.size > 1000) seen.current = new Set([...seen.current].slice(-500));

        if (!n.is_read) {
            setSummary(s => (s ? {
                ...s,
                unread: s.unread + 1,
                total: (s.total || 0) + 1,
                unread_critical: s.unread_critical + (n.priority === 'critical' ? 1 : 0),
                unread_high: (s.unread_high || 0) + (n.priority === 'high' ? 1 : 0),
                ack_pending: s.ack_pending + (n.requires_ack && !n.acknowledged_at ? 1 : 0),
                by_category: { ...s.by_category, [n.category]: (s.by_category?.[n.category] || 0) + 1 },
            } : s));
        }
        try {
            window.dispatchEvent(new window.CustomEvent('flowsense:notify', { detail: n }));
            if (n.source === 'sla_ai') window.dispatchEvent(new window.CustomEvent('flowsense:sla-ai-inbox', { detail: { source: 'notify', notification: n } }));
        } catch (_) { /* old browsers */ }

        if (payload.silent || payload.replay || n.is_read) return;

        // --- UPDATE (toast once)
        if (!tabVisible()) {
            // No tab on screen: browser push (service worker) shows it. Without browser push on this
            // device, ONE tab raises a plain OS notification — claimed so other tabs do not repeat it.
            if (webPush.permission() === 'granted' && !webPush.isEnabledHere() && ledger.claim(`os:${n.id}`, ID_TTL_MS)) {
                try {
                    const dn = new window.Notification(n.title, { body: n.body, tag: n.group_key || n.id, requireInteraction: n.priority === 'critical' });
                    dn.onclick = () => { window.focus(); openNotification(n); dn.close(); };
                } catch (_) { /* some browsers only allow notifications from a service worker */ }
            }
            return;
        }
        if (!ledger.claim(`id:${n.id}`, ID_TTL_MS)) return;            // already shown (other tab / before reload)
        if (!ledger.claim(fingerprint(n), REPEAT_TTL_MS)) return;      // same message again → badge only
        // --- END UPDATE
        if (payload.sound && (n.priority === 'critical' || n.priority === 'high')) playTone(n.priority === 'critical');
        showToast(n);
    }, [showToast, openNotification]);

    const handleNewRef = useRef(handleNew);
    handleNewRef.current = handleNew;

    useEffect(() => {
        if (!enabled) return undefined;
        const stop = notifyStream.start();
        const offs = [
            notifyStream.on('status', (s) => { if (isMounted.current) setStatus(s); }),
            notifyStream.on('hello', (h) => { if (h?.summary && isMounted.current) setSummary(h.summary); }),
            notifyStream.on('summary', (s) => { if (s && isMounted.current) setSummary(s); }),
            notifyStream.on('notification', (p) => handleNewRef.current(p)),
            // --- UPDATE (toast once): read / acknowledged / archived anywhere → close its toast here
            notifyStream.on('notification_update', (c) => {
                if (!c) return;
                if (c.action === 'read_all') { [...openToasts.current].forEach(id => toastRef.current.close(`notify-${id}`)); return; }
                if (!['read', 'acknowledged', 'archived', 'deleted', 'snoozed'].includes(c.action)) return;
                [].concat(c.ids || []).forEach(id => { if (openToasts.current.has(id)) toastRef.current.close(`notify-${id}`); });
            }),
            notifyStream.on('poll', refreshSummary),
            notifyStream.on('visible', refreshSummary),
        ];
        setStatus(notifyStream.getStatus());
        refreshSummary();
        // --- UPDATE (toast once): retire the old Firebase web worker (it showed a second pop-up for every
        // message) and move browsers that already allowed notifications to FlowSense browser push.
        const sw0 = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
        sw0?.getRegistrations?.().then((regs) => {
            regs.filter(r => /firebase-messaging-sw/.test(r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || ''))
                .forEach(r => r.unregister().catch(() => {}));
        }).catch(() => {});
        if (webPush.isSupported() && webPush.permission() === 'granted' && !webPush.isEnabledHere()) {
            webPush.enable().catch(() => { /* no VAPID key / blocked — the live stream still works */ });
        }
        // --- END UPDATE
        const onSwMessage = (ev) => {
            const m = ev?.data;
            if (m?.type === 'flowsense:notify-open') navigateTo(m.url ? new URL(m.url).pathname + new URL(m.url).search : null);
            if (m?.type === 'flowsense:notify-resubscribe' && webPush.isEnabledHere()) webPush.enable().catch(() => {});
        };
        const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
        sw?.addEventListener?.('message', onSwMessage);
        return () => { offs.forEach(off => off()); stop(); sw?.removeEventListener?.('message', onSwMessage); };
    }, [enabled, refreshSummary, navigateTo]);

    // "(3) FlowSense" tab title
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const unread = summary?.unread || 0;
        const base = baseTitle.current || document.title.replace(/^\(\d+\+?\)\s*/, '');
        document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${base}` : base;
    }, [summary?.unread]);

    const value = useMemo(() => ({
        summary,
        status,
        isCenterOpen: centreOpen,
        openCenter,
        closeCenter: centreOnClose,
        refreshSummary,
        navigateTo,
    }), [summary, status, centreOpen, centreOnClose, openCenter, refreshSummary, navigateTo]);

    return (
        <NotifyContext.Provider value={value}>
            {children}
            {centreMounted && (
                <NotificationCenter isOpen={centreOpen} onClose={centreOnClose} summary={summary} status={status} onNavigate={navigateTo} />
            )}
        </NotifyContext.Provider>
    );
}

export default NotifyProvider;
